import type {JSONContent} from '@tiptap/core';
import {automationVariableToken, type AutomationVariableReference} from '../../../config/variableReferences';

/** HTML is source text here. Never pass it to Tiptap's HTML or Markdown parser. */
export const automationTextContent = (text: string, {placeholders = true}: {placeholders?: boolean} = {}): JSONContent[] => {
    const content: JSONContent[] = [];
    const pattern = /\{\{"([^"\r\n]+)"\.([a-zA-Z_][a-zA-Z0-9_.]*(?:\[\d+\][a-zA-Z0-9_.]*)*)\}\}|\[\[([^\[\]\r\n]+)\]\]|\n/g;
    let start = 0;

    for (const match of Array.from(text.matchAll(pattern))) {
        // Only email authoring resolves aliases. In other fields [[text]] stays ordinary text.
        if (match[3] !== undefined && !placeholders) continue;
        const index = match.index!;
        if (index > start) content.push({type: 'text', text: text.slice(start, index)});
        content.push(match[0] === '\n'
            ? {type: 'hardBreak'}
            : match[3] !== undefined
                ? {type: 'automationPlaceholder', attrs: {key: match[3]}}
                : {type: 'automationVariable', attrs: {nodeName: match[1], name: match[2]}});
        start = index + match[0].length;
    }
    if (start < text.length) content.push({type: 'text', text: text.slice(start)});
    return content;
};

export const automationTextDocument = (text: string, options?: {placeholders?: boolean}): JSONContent => ({
    type: 'doc',
    content: [{type: 'paragraph', content: automationTextContent(text, options)}],
});

/** A mapping can resolve one whole step reference; surrounding text remains a literal value. */
export const readAutomationVariableReference = (value: string): AutomationVariableReference | null => {
    const content = automationTextContent(value.trim());
    return content.length === 1 && content[0].type === 'automationVariable'
        ? content[0].attrs as AutomationVariableReference
        : null;
};

export const serializeAutomationText = (node: JSONContent): string => {
    if (node.type === 'text') return node.text ?? '';
    if (node.type === 'hardBreak') return '\n';
    if (node.type === 'automationPlaceholder') return `[[${node.attrs?.key}]]`;
    if (node.type === 'automationVariable') {
        return automationVariableToken(node.attrs as AutomationVariableReference);
    }
    return (node.content ?? []).map(serializeAutomationText).join('');
};
